import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import kittyCareer from '../assets/kitty-career.png';
import basicKitty from '../assets/basic-kitty.png';

const Career = () => {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [activeNav, setActiveNav] = useState(2); // Career page is index 2
  const observerRef = useRef();

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems(prev => new Set([...prev, entry.target.dataset.index]));
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -100px 0px' }
    );

    const elements = document.querySelectorAll('[data-index]');
    elements.forEach(el => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  const timelineData = [
    {
      year: "2024",
      title: "Chemical Engineering Major",
      institution: "University of Maryland Baltimore County",
      description: "Pursuing Bachelor's degree in Chemical Engineering with focus on process optimization and sustainability. Currently maintaining a 3.8 GPA while actively participating in research projects.",
      type: "education",
      achievements: ["Dean's List Recognition", "Research Assistant Position", "Chemical Engineering Society Member"]
    },
    {
      year: "2023",
      title: "Photography Business Launch",
      institution: "Freelance Photographer",
      description: "Launched professional photography business specializing in portraits, lifestyle, and event photography. Built client base through social media marketing and referrals.",
      type: "work",
      achievements: ["50+ Happy Clients", "Featured in Local Magazine", "Photography Awards"]
    },
    {
      year: "2022",
      title: "Fitness & Wellness Journey",
      institution: "Personal Achievement",
      description: "Began serious fitness journey, achieving impressive milestones including 405lb bench press. Developed expertise in nutrition and workout planning.",
      type: "personal",
      achievements: ["405lb Bench Press", "Certified Personal Trainer", "Nutrition Specialist"]
    },
    {
      year: "2021",
      title: "Academic Excellence",
      institution: "High School Graduation",
      description: "Graduated with honors and received multiple academic awards. Demonstrated leadership in various extracurricular activities.",
      type: "education",
      achievements: ["Valedictorian", "Science Fair Winner", "Student Council President"]
    }
  ];

  const projectsData = [
    {
      title: "Chemical Process Optimization",
      category: "Engineering",
      description: "Developed innovative solutions for improving chemical manufacturing processes, resulting in 15% efficiency increase.",
      technologies: ["MATLAB", "Aspen Plus", "Python"],
      status: "Completed",
      image: "🧪"
    },
    {
      title: "Portrait Photography Portfolio",
      category: "Photography",
      description: "Created stunning portrait collection showcasing diverse subjects and lighting techniques.",
      technologies: ["Adobe Lightroom", "Photoshop", "Professional Lighting"],
      status: "Ongoing",
      image: "📸"
    },
    {
      title: "Fitness Training Program",
      category: "Personal Development",
      description: "Designed comprehensive training program combining strength training with nutrition guidance.",
      technologies: ["MyFitnessPal", "Training Apps", "Nutrition Planning"],
      status: "Completed",
      image: "💪"
    },
    {
      title: "Sustainable Living Initiative",
      category: "Environmental",
      description: "Led campus initiative promoting sustainable practices and environmental awareness.",
      technologies: ["Research", "Presentations", "Community Outreach"],
      status: "Ongoing",
      image: "🌱"
    }
  ];

  const getTypeColor = (type) => {
    switch (type) {
      case 'education': return { bg: 'rgba(59, 130, 246, 0.1)', border: '#3b82f6', icon: '🎓' };
      case 'work': return { bg: 'rgba(219, 39, 119, 0.1)', border: '#db2777', icon: '💼' };
      case 'personal': return { bg: 'rgba(168, 85, 247, 0.1)', border: '#a855f7', icon: '⭐' };
      default: return { bg: 'rgba(156, 163, 175, 0.1)', border: '#9ca3af', icon: '📍' };
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ef 25%, #fdf2f8 50%, #f1f5f9 75%, #fdf4ff 100%)',
      fontFamily: "'Playfair Display', 'Georgia', serif",
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Floating background elements */}
      {Array.from({ length: 15 }, (_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: Math.random() * 8 + 6 + 'px',
            height: Math.random() * 8 + 6 + 'px',
            borderRadius: '50%',
            background: `linear-gradient(45deg, 
              rgba(219, 39, 119, ${0.1 + Math.random() * 0.3}), 
              rgba(168, 85, 247, ${0.1 + Math.random() * 0.3}))`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `career-float ${4 + Math.random() * 6}s ease-in-out infinite`,
            animationDelay: Math.random() * 3 + 's',
          }}
        />
      ))}

      {/* Navigation Bar */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        zIndex: 1000,
        padding: '1rem 2rem',
        borderBottom: '1px solid rgba(219, 39, 119, 0.1)',
        transition: 'all 0.3s ease'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <img 
              src={basicKitty} 
              alt="Kitty Logo" 
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #db2777',
                boxShadow: '0 2px 8px rgba(219, 39, 119, 0.2)'
              }} 
            />
            <div style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              background: 'linear-gradient(45deg, #db2777, #a855f7, #ec4899)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.5px'
            }}>MC</div>
          </div>
          <div style={{ display: 'flex', gap: '2rem' }}>
            {[
              { name: 'Home', path: '/' },
              { name: 'Gallery', path: '/gallery' },
              { name: 'Career', path: '/career' },
              { name: 'Admin', path: '/admin' }
            ].map((item, index) => (
              <Link 
                key={item.name} 
                to={item.path}
                style={{
                  cursor: 'pointer',
                  fontWeight: '500',
                  color: activeNav === index ? '#db2777' : '#64748b',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  padding: '0.5rem 0',
                  textDecoration: 'none'
                }}
                onMouseEnter={() => setActiveNav(index)}
                onMouseLeave={() => setActiveNav(2)} // Always return to Career being active
              >
                {item.name}
                {activeNav === index && (
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    height: '2px',
                    background: 'linear-gradient(90deg, #db2777, #ec4899)',
                    borderRadius: '1px'
                  }} />
                )}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <section style={{
        padding: '8rem 2rem 2rem 2rem',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '2rem',
          marginBottom: '2rem',
          flexWrap: 'wrap'
        }}>
          <div style={{
            position: 'relative',
            padding: '1rem',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '1.5rem',
            boxShadow: '0 10px 30px rgba(219, 39, 119, 0.2)',
            border: '2px solid rgba(219, 39, 119, 0.1)',
            animation: 'gentle-bounce 3s ease-in-out infinite'
          }}>
            <img 
              src={kittyCareer} 
              alt="Career Kitty" 
              style={{
                width: '100px',
                height: '100px',
                objectFit: 'contain',
                display: 'block'
              }} 
            />
            
          </div>
          <div>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: '700',
              margin: '0',
              background: 'linear-gradient(45deg, #db2777, #a855f7, #ec4899)',
              backgroundSize: '200% 200%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'gradient-shift 4s ease-in-out infinite'
            }}>My Career Journey</h1>
            <p style={{
              fontSize: '1.3rem',
              color: '#64748b',
              fontStyle: 'italic',
              margin: '0.5rem 0 0 0'
            }}>Career Timeline</p>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '2rem',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          position: 'relative',
          paddingLeft: '2rem'
        }}>
          {/* Timeline Line */}
          <div style={{
            position: 'absolute',
            left: '1rem',
            top: '0',
            bottom: '0',
            width: '3px',
            background: 'linear-gradient(180deg, #db2777, #a855f7, #ec4899)',
            borderRadius: '2px',
            opacity: 0.7
          }} />

          {timelineData.map((item, index) => {
            const typeStyle = getTypeColor(item.type);
            const isVisible = visibleItems.has(index.toString());
            
            return (
              <div
                key={index}
                data-index={index}
                style={{
                  position: 'relative',
                  marginBottom: '4rem',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateX(0) rotateY(0deg) scale(1)' : 'translateX(-100px) rotateY(-15deg) scale(0.8)',
                  transition: 'all 1.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  transitionDelay: `${index * 0.3}s`,
                  filter: isVisible ? 'blur(0px)' : 'blur(5px)'
                }}
              >
                {/* Timeline Dot */}
                <div style={{
                  position: 'absolute',
                  left: '-2.2rem',
                  top: '1rem',
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '50%',
                  background: typeStyle.bg,
                  border: `3px solid ${typeStyle.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  boxShadow: `0 4px 15px ${typeStyle.border}30`,
                  zIndex: 2,
                  animation: isVisible ? `timeline-dot-appear 0.8s ease-out ${index * 0.3 + 0.5}s both, dot-pulse 3s ease-in-out infinite ${index * 0.3 + 1.5}s` : 'none',
                  transform: isVisible ? 'scale(1) rotate(0deg)' : 'scale(0) rotate(-180deg)',
                  transition: 'all 0.6s ease-out'
                }}>
                  {typeStyle.icon}
                  {/* Ripple effect */}
                  <div style={{
                    position: 'absolute',
                    inset: '-10px',
                    borderRadius: '50%',
                    border: `2px solid ${typeStyle.border}`,
                    opacity: isVisible ? 0.3 : 0,
                    animation: isVisible ? `ripple 2s ease-out infinite ${index * 0.3 + 2}s` : 'none'
                  }} />
                </div>

                {/* Content Card */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '1.5rem',
                  padding: '2rem',
                  boxShadow: isVisible ? '0 20px 60px rgba(0, 0, 0, 0.15)' : '0 10px 40px rgba(0, 0, 0, 0.1)',
                  border: `1px solid ${typeStyle.border}30`,
                  marginLeft: '1rem',
                  position: 'relative',
                  overflow: 'hidden',
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.8s ease-out',
                  transitionDelay: `${index * 0.3 + 0.2}s`
                }}>
                  {/* Card Accent */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${typeStyle.border}, ${typeStyle.border}80)`
                  }} />

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '1rem'
                  }}>
                    <div>
                      <div style={{
                        display: 'inline-block',
                        background: typeStyle.bg,
                        color: typeStyle.border,
                        padding: '0.3rem 0.8rem',
                        borderRadius: '1rem',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        border: `1px solid ${typeStyle.border}50`
                      }}>
                        {item.year}
                      </div>
                      <h3 style={{
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        color: '#1f2937',
                        margin: '0 0 0.5rem 0'
                      }}>{item.title}</h3>
                      <p style={{
                        fontSize: '1.1rem',
                        color: '#6b7280',
                        fontWeight: '600',
                        margin: 0
                      }}>{item.institution}</p>
                    </div>
                  </div>

                  <p style={{
                    fontSize: '1rem',
                    color: '#374151',
                    lineHeight: 1.6,
                    marginBottom: '1.5rem'
                  }}>{item.description}</p>

                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    {item.achievements.map((achievement, i) => (
                      <span
                        key={i}
                        style={{
                          background: 'linear-gradient(45deg, rgba(219, 39, 119, 0.1), rgba(168, 85, 247, 0.1))',
                          color: '#db2777',
                          padding: '0.4rem 0.8rem',
                          borderRadius: '0.8rem',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          border: '1px solid rgba(219, 39, 119, 0.2)'
                        }}
                      >
                        ✨ {achievement}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Projects Section */}
      <section style={{
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px)',
        margin: '4rem 2rem',
        borderRadius: '2rem',
        padding: '4rem 2rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            background: 'linear-gradient(45deg, #db2777, #a855f7)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '1rem'
          }}>Featured Projects</h2>
          <p style={{
            fontSize: '1.2rem',
            color: '#64748b',
            fontStyle: 'italic'
          }}>Showcasing innovation and creativity</p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {projectsData.map((project, index) => (
            <div
              key={index}
              data-index={`project-${index}`}
              style={{
                background: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '1.5rem',
                padding: '2rem',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(219, 39, 119, 0.1)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                opacity: visibleItems.has(`project-${index}`) ? 1 : 0,
                transform: visibleItems.has(`project-${index}`) ? 'translateY(0)' : 'translateY(30px)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-10px)';
                e.target.style.boxShadow = '0 15px 50px rgba(219, 39, 119, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={{
                fontSize: '3rem',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                {project.image}
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: 0
                }}>{project.title}</h3>
                <span style={{
                  background: project.status === 'Completed' 
                    ? 'linear-gradient(45deg, #10b981, #34d399)' 
                    : 'linear-gradient(45deg, #f59e0b, #fbbf24)',
                  color: 'white',
                  padding: '0.3rem 0.8rem',
                  borderRadius: '1rem',
                  fontSize: '0.8rem',
                  fontWeight: '600'
                }}>
                  {project.status}
                </span>
              </div>

              <p style={{
                color: '#db2777',
                fontWeight: '600',
                fontSize: '0.9rem',
                marginBottom: '1rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>{project.category}</p>

              <p style={{
                color: '#374151',
                lineHeight: 1.5,
                marginBottom: '1.5rem'
              }}>{project.description}</p>

              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}>
                {project.technologies.map((tech, i) => (
                  <span
                    key={i}
                    style={{
                      background: 'rgba(219, 39, 119, 0.1)',
                      color: '#db2777',
                      padding: '0.3rem 0.6rem',
                      borderRadius: '0.5rem',
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

    

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap');
        
        @keyframes career-float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-25px) rotate(180deg); }
        }
        
        @keyframes gentle-bounce {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes timeline-dot-appear {
          0% { transform: scale(0) rotate(-180deg); opacity: 0; }
          50% { transform: scale(1.2) rotate(-90deg); opacity: 0.8; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        
        @keyframes dot-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); box-shadow: 0 0 20px rgba(219, 39, 119, 0.4); }
        }
        
        @keyframes ripple {
          0% { transform: scale(1); opacity: 0.3; }
          100% { transform: scale(2); opacity: 0; }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        * {
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};

export default Career;